<script lang="ts">
	import { format } from 'svelte-i18n';

	import { onMount } from 'svelte';

	import type { Setting } from 'src/types/Settings';

	import CurrentPlanContainer from 'src/components/Settings/CurrentPlanContainer.svelte';
	import CurrentPlanContainerV2 from 'src/components/Settings/CurrentPlanContainerV2.svelte';

	import { Collapse, Container, Stack, Text } from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	import { capFirstChar } from 'src/helpers/utilities';
	import { goToSpecificUserSetting } from 'src/helpers/user_settings_helpers';

	export let config: Setting[];
	export let variant: 'legacy' | 'v2';
	/** Extra class applied only to v2 group containers. Lets AppSettings keep its `mb-s` group margin while UserSettings stays unaffected. */
	export let groupContainerClass = '';

	let windowClientWidth: number;
	let collapsesExpanded: { [key: string]: boolean } = {};
	const allItemsRefs: { [key: string]: HTMLElement } = {};

	const isVisible = (item: { visible?: () => boolean }): boolean => item.visible?.() ?? true;

	const getVisibleGroups = (section: Setting) =>
		(section.groups ?? [])
			.filter(isVisible)
			.map((group) => ({
				group,
				items: (group.components ?? []).filter(isVisible),
			}))
			.filter(({ items }) => items.length > 0);

	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
	$: visibleSections = config.filter(isVisible);

	// Toggles a Collapse. When collapsing a parent section, also force-close
	// all of its child group collapses.
	const toggleCollapse = (setting: Setting): void => {
		const wasExpanded = collapsesExpanded[setting.id] === true;
		const next = { ...collapsesExpanded, [setting.id]: !wasExpanded };
		if (wasExpanded) {
			for (const group of setting.groups ?? []) {
				next[group.id] = false;
			}
		}
		collapsesExpanded = next;
	};

	onMount(() => {
		if (variant === 'legacy') {
			const next = goToSpecificUserSetting(allItemsRefs, collapsesExpanded);
			if (next) collapsesExpanded = next;
		} else {
			goToSpecificUserSetting(allItemsRefs);
		}
	});
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

{#if variant === 'legacy'}
	{#each visibleSections as section (section.id)}
		<div
			class={classMap({
				'mb-s': true,
				'd-flex gap-s': !!section.lineContainer,
				'flex-direction-column': isMobile && !!section.lineContainer,
			})}
			bind:this={allItemsRefs[section.id]}
		>
			<Container noScroll class="flex-1 min-width-0">
				<Text element="h2" class="mb-s text-capitalize">
					{$format(section.title, { default: section.titleDefault })}
				</Text>

				{#if section.subtitle}
					<Text styleType="body-small" class="mb-m text-color-muted">
						{capFirstChar($format(section.subtitle, { default: section.subtitleDefault }))}
					</Text>
				{/if}

				{#if section.component}
					<div>
						<svelte:component this={section.component} {...section.componentProps ?? {}} />
					</div>
				{/if}

				{#if section.groups?.length}
					<Stack gap="s">
						{#each section.groups.filter(isVisible) as child (child.id)}
							<div class="d-flex min-width-0" bind:this={allItemsRefs[child.id]}>
								<Collapse
									id={`collapse-${child.id}`}
									class="width-full"
									title={capFirstChar(
										$format(child.title, { default: child.titleDefault }),
									)}
									expanded={collapsesExpanded[child.id]}
									on:click={() => toggleCollapse(child)}
								>
									{#if child.component}
										<svelte:component
											this={child.component}
											{...child.componentProps ?? {}}
										/>
									{:else if child.components?.length}
										<Stack gap="s">
											{#each child.components.filter(isVisible) as item (item.id)}
												<div
													class="min-width-0"
													bind:this={allItemsRefs[item.id]}
												>
													<svelte:component
														this={item.component}
														{...item.componentProps ?? {}}
													/>
												</div>
											{/each}
										</Stack>
									{/if}
								</Collapse>
							</div>
						{/each}
					</Stack>
				{/if}
			</Container>

			{#if section.lineContainer}
				<CurrentPlanContainer {isMobile} />
			{/if}
		</div>
	{/each}
{:else}
	{#each visibleSections as section (section.id)}
		<div
			class={classMap({
				'mb-s': true,
				'd-flex gap-s': !!section.lineContainer,
				'flex-direction-column': isMobile && !!section.lineContainer,
			})}
			bind:this={allItemsRefs[section.id]}
		>
			<Container type="borderSolid" noScroll class="flex-1 min-width-0">
				<Text element="h3" class="mb-s text-capitalize">
					{$format(section.title, { default: section.titleDefault })}
				</Text>

				{#if section.subtitle}
					<Text styleType="body-small" class="mb-m text-color-muted">
						{capFirstChar($format(section.subtitle, { default: section.subtitleDefault }))}
					</Text>
				{/if}

				{#if section.component}
					<div>
						<svelte:component this={section.component} {...section.componentProps ?? {}} />
					</div>
				{/if}

				{@const visibleGroups = getVisibleGroups(section)}
				{#if visibleGroups.length}
					<Stack gap={isMobile ? 8 : 16}>
						{#each visibleGroups as { group, items } (group.id)}
							<Container
								type="borderSolid"
								noScroll
								padding="0"
								class={groupContainerClass}
							>
								{#if group.title}
									<div class="pb-m pt-m pl-m">
										<Text element="h4" class="text-capitalize">
											{capFirstChar(
												$format(group.title, {
													default: group.titleDefault,
												}),
											)}
										</Text>
										{#if group.subtitle}
											<Text styleType="body-small" class="mt-s text-color-muted">
												{capFirstChar(
													$format(group.subtitle, {
														default: group.subtitleDefault,
													}),
												)}
											</Text>
										{/if}
									</div>
									<hr class="hr-color-2 mt-0 mb-0" />
								{/if}

								<Stack gap={0} class="ml-m mr-m">
									{#each items as item, i (item.id)}
										{@const hasMore = i < items.length - 1}
										<div class="min-width-0" bind:this={allItemsRefs[item.id]}>
											<div class="pt-m pb-m">
												{#if ['changeEmail', 'changePass'].includes(item.id)}
													<svelte:component
														this={item.component}
														{isMobile}
														{...item.componentProps ?? {}}
													/>
												{:else}
													<svelte:component
														this={item.component}
														{...item.componentProps ?? {}}
													/>
												{/if}
											</div>
											{#if hasMore}
												<hr class="hr-color-2 mt-0 mb-0" />
											{/if}
										</div>
									{/each}
								</Stack>
							</Container>
						{/each}
					</Stack>
				{/if}
			</Container>

			{#if section.lineContainer}
				<CurrentPlanContainerV2 {isMobile} />
			{/if}
		</div>
	{/each}
{/if}
